import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { X, Loader2, CheckCircle, XCircle, Sparkles, ShieldCheck } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useUiStore } from '../store/uiStore'
import api from '../lib/api'
import { useFocusTrap } from '../lib/useFocusTrap'
import { toast } from '../lib/toast'
import SocialButtons from './SocialButtons'
import Turnstile, { turnstileEnabled } from './Turnstile'
import { seznamConversionHit } from '../lib/seznamRetargeting'
import { googleAdsConversion } from '../lib/googleAnalytics'


const inputClass = 'w-full px-4 py-3 border border-white/10 rounded-lg bg-white/5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm'
const btnClass = 'w-full bg-gradient-to-r from-primary-600 to-primary-500 text-white py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm'

/** Druhý krok loginu: zadání 2FA kódu (TOTP / e-mail / recovery). */
function TwoFactorStep({ method, twofaToken, onBack }: { method?: 'totp' | 'email'; twofaToken: string; onBack: () => void }) {
  const { t } = useTranslation()
  const { verifyTwofa } = useAuthStore()
  const { closeAuthModal } = useUiStore()
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await verifyTwofa(twofaToken, code.trim())
      closeAuthModal()
      toast.success(t('auth.signed_in', { defaultValue: 'Přihlášení proběhlo úspěšně' }))
      navigate('/dashboard')
    } catch (err: any) {
      setError(err.response?.data?.message || t('twofa.code_invalid', { defaultValue: 'Neplatný nebo expirovaný kód.' }))
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 text-primary-400">
        <ShieldCheck className="w-5 h-5" />
        <span className="text-sm font-semibold">{t('twofa.step_title', { defaultValue: 'Dvoufázové ověření' })}</span>
      </div>
      <p className="text-gray-400 text-sm">
        {method === 'email'
          ? t('twofa.step_email', { defaultValue: 'Zadejte 6místný kód, který jsme poslali na váš e-mail.' })
          : t('twofa.step_totp', { defaultValue: 'Zadejte 6místný kód z vaší autentizační aplikace.' })}
      </p>
      {error && <div className="bg-red-500/15 border border-red-500/20 text-red-300 px-4 py-3 rounded-lg text-sm">{error}</div>}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('twofa.code_label', { defaultValue: 'Ověřovací kód' })}</label>
        <input
          type="text" inputMode="text" autoFocus value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456"
          className={`${inputClass} text-center text-lg tracking-[0.3em] font-mono`}
          required
        />
        <p className="text-xs text-gray-500 mt-1.5">{t('twofa.recovery_hint', { defaultValue: 'Nemáte přístup? Použijte záložní kód.' })}</p>
      </div>
      <button type="submit" disabled={loading} className={btnClass}>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {t('twofa.verify_button', { defaultValue: 'Ověřit a přihlásit' })}
      </button>
      <div className="text-center">
        <button type="button" onClick={onBack} className="text-sm text-gray-400 hover:text-primary-400 transition">{t('auth.back_to_login')}</button>
      </div>
    </form>
  )
}

function LoginForm({ onForgot }: { onForgot: () => void }) {
  const { t } = useTranslation()
  const { login } = useAuthStore()
  const { closeAuthModal } = useUiStore()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [captcha, setCaptcha] = useState('')
  const [challenge, setChallenge] = useState<{ method?: 'totp' | 'email'; token: string } | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const res = await login(email, password, remember, captcha)
      if (res.twofaRequired && res.twofaToken) {
        setChallenge({ method: res.method, token: res.twofaToken })
        return
      }
      closeAuthModal()
      toast.success(t('auth.signed_in', { defaultValue: 'Přihlášení proběhlo úspěšně' }))
      navigate('/dashboard')
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.login_error'))
    } finally {
      setLoading(false)
    }
  }

  if (challenge) {
    return <TwoFactorStep method={challenge.method} twofaToken={challenge.token} onBack={() => setChallenge(null)} />
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="bg-red-500/15 border border-red-500/20 text-red-300 px-4 py-3 rounded-lg text-sm">{error}</div>}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth.email')}</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth.password')}</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} required />
      </div>
      <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-300">
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          className="w-4 h-4 rounded border-white/20 bg-white/5 text-primary-600 focus:ring-primary-500 cursor-pointer"
        />
        {t('auth.remember_me', { defaultValue: 'Zapamatovat přihlášení (zůstat přihlášen měsíc)' })}
      </label>
      <Turnstile onVerify={setCaptcha} theme="dark" className="flex justify-center" />
      <button type="submit" disabled={loading || (turnstileEnabled() && !captcha)} className={btnClass}>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {t('auth.login_button')}
      </button>
      <div className="text-center">
        <button type="button" onClick={onForgot} className="text-sm text-gray-400 hover:text-primary-400 transition">
          {t('auth.forgot_password')}
        </button>
      </div>
    </form>
  )
}

function RegisterForm() {
  const { t } = useTranslation()
  const { register } = useAuthStore()
  const { closeAuthModal } = useUiStore()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [captcha, setCaptcha] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await register(email, password, name || undefined, captcha)
      seznamConversionHit() // Sklik konverze: registrace uživatele (gated souhlasem)
      googleAdsConversion('register') // Google Ads konverze: registrace (gated Consent Mode)
      closeAuthModal()
      toast.success(t('auth.welcome_registered', { defaultValue: 'Účet vytvořen, vítejte!' }))
      navigate('/dashboard')
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.register_error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="bg-red-500/15 border border-red-500/20 text-red-300 px-4 py-3 rounded-lg text-sm">{error}</div>}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth.name')}</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth.email')}</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth.password')}</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} className={inputClass} required placeholder={t('auth.reset_min_chars')} />
      </div>
      <Turnstile onVerify={setCaptcha} theme="dark" className="flex justify-center" />
      <button type="submit" disabled={loading || (turnstileEnabled() && !captcha)} className={btnClass}>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {t('auth.register_button')}
      </button>
    </form>
  )
}

function ForgotForm({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await api.post('/auth/forgot-password', { email })
      setSent(true)
    } catch (err: any) {
      setError(err.response?.data?.message || t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  if (sent) {
    return (
      <div className="text-center py-4">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">{t('auth.forgot_sent_title')}</h3>
        <p className="text-gray-400 text-sm mb-6" dangerouslySetInnerHTML={{ __html: t('auth.forgot_sent_desc', { email }) }} />
        <button onClick={onBack} className="text-primary-400 hover:underline text-sm font-medium">{t('auth.back_to_login')}</button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-gray-400 text-sm">{t('auth.forgot_desc')}</p>
      {error && <div className="bg-red-500/15 border border-red-500/20 text-red-300 px-4 py-3 rounded-lg text-sm">{error}</div>}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth.email')}</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required />
      </div>
      <button type="submit" disabled={loading} className={btnClass}>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {t('auth.forgot_button')}
      </button>
      <div className="text-center">
        <button type="button" onClick={onBack} className="text-sm text-gray-400 hover:text-primary-400 transition">{t('auth.back_to_login')}</button>
      </div>
    </form>
  )
}

function ResetForm({ token, onLogin }: { token: string; onLogin: () => void }) {
  const { t } = useTranslation()
  const { closeAuthModal } = useUiStore()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  if (!token) {
    return (
      <div className="text-center py-4">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">{t('auth.reset_invalid_title')}</h3>
        <p className="text-gray-400 text-sm mb-6">{t('auth.reset_invalid_desc')}</p>
        <button onClick={onLogin} className="text-primary-400 hover:underline text-sm font-medium">{t('auth.request_new_link')}</button>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password.length < 8) { setError(t('auth.reset_password_short')); return }
    if (password !== passwordConfirm) { setError(t('auth.reset_password_mismatch')); return }
    setLoading(true)
    try {
      await api.post('/auth/reset-password', { token, password })
      setSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.message || t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="text-center py-4">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">{t('auth.reset_success_title')}</h3>
        <p className="text-gray-400 text-sm mb-6">{t('auth.reset_success_desc')}</p>
        <button onClick={() => { closeAuthModal(); navigate('/') }} className={btnClass}>
          {t('auth.login_button')}
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-gray-400 text-sm">{t('auth.reset_desc')}</p>
      {error && <div className="bg-red-500/15 border border-red-500/20 text-red-300 px-4 py-3 rounded-lg text-sm">{error}</div>}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth.new_password')}</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder={t('auth.reset_min_chars')} required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth.confirm_password')}</label>
        <input type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} className={inputClass} required />
      </div>
      <button type="submit" disabled={loading} className={btnClass}>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {t('auth.reset_button')}
      </button>
    </form>
  )
}

export default function AuthModal() {
  const { t } = useTranslation()
  const { authModal, closeAuthModal, openAuthModal } = useUiStore()
  const { open, tab, resetToken } = authModal
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, closeAuthModal, open)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeAuthModal() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, closeAuthModal])

  if (!open) return null

  const showTabs = tab === 'login' || tab === 'register'
  const headingMap: Record<string, string> = {
    forgot: t('auth.forgot_title'),
    reset: t('auth.reset_title'),
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop: click-outside-to-close only (modal has a visible close button); not announced to screen readers */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeAuthModal} aria-hidden="true" />

      <div ref={dialogRef} role="dialog" aria-modal="true" className="relative w-full max-w-md glass-dark border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center shadow-lg shadow-primary-500/25">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="font-bold text-white text-sm">SEO Tools</span>
            </div>
            <button onClick={closeAuthModal} className="text-gray-500 hover:text-gray-300 transition p-1 rounded-lg hover:bg-white/5">
              <X className="w-5 h-5" />
            </button>
          </div>

          {showTabs ? (
            <div className="flex bg-white/5 rounded-xl p-1 mb-2 relative">
              {(['login', 'register'] as const).map((t_) => (
                <button
                  key={t_}
                  onClick={() => openAuthModal(t_)}
                  className={`relative flex-1 py-2 text-sm font-semibold rounded-lg z-10 transition-colors ${tab === t_ ? 'text-white' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  {t_ === 'login' ? t('nav.login') : t('nav.register')}
                  {tab === t_ && (
                    <motion.span
                      layoutId="auth-tab-pill"
                      className="absolute inset-0 bg-primary-600 rounded-lg shadow -z-10"
                      transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                    />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <h2 className="text-xl font-bold text-white mb-1">{headingMap[tab]}</h2>
          )}
        </div>

        {/* Body */}
        <div className="px-6 pb-6">
          {tab === 'login' && <LoginForm onForgot={() => openAuthModal('forgot')} />}
          {tab === 'register' && <RegisterForm />}
          {(tab === 'login' || tab === 'register') && <SocialButtons next="/dashboard" />}
          {tab === 'forgot' && <ForgotForm onBack={() => openAuthModal('login')} />}
          {tab === 'reset' && <ResetForm token={resetToken || ''} onLogin={() => openAuthModal('forgot')} />}
        </div>
      </div>
    </div>
  )
}
